import {css, CSSResult, html, LitElement, nothing, unsafeCSS,} from 'lit';
import {customElement, property} from 'lit/decorators.js';
import {classMap} from 'lit/directives/class-map.js';
import bellIcon from '@breuni-ui/bdk/dist/design-tokens/images/icons/o-ic-16_bell.svg';
import {consume} from '@lit/context';
import styles from './size-layer-item.scss';
import typographyStyles from '../../../styles/responsive-nui-typography.scss';
import {Article, Messages, SizeSelectionContainer} from '../../../@types/wishlist.js';
import {trackSizeSelected} from '../../../scripts/service/cube-tracking-layer.js';
import {sizeLayerContext, SizeLayerState} from '../../../scripts/context/sizeLayerContext.js';
import {ProductIdentifier} from '../../../scripts/service/product-identifier.js';
import {i18nContext} from '../../../scripts/context/i18nContext.js';
import {hideIconFromAssistiveTechnology} from '../icon/icon-accessibility.js';

@customElement('ents-size-layer-item')
export class SizeLayerItem extends LitElement {
  @property({type: Boolean, reflect: true}) private active: boolean = false;

  @property({type: Number}) private syan: number = 0;

  @property({type: String, attribute: 'color-id'}) private colorId?: string = undefined;

  @property({type: Boolean}) private addToCartAfterSizeSelection: boolean = false;

  @property({type: Object}) private article!: Article;

  @property({type: Boolean}) private showPrices: boolean = false;

  @consume({context: i18nContext})
  protected messages!: Messages;

  @consume({context: sizeLayerContext, subscribe: true})
  @property({attribute: false})
  private sizeLayerState!: SizeLayerState;

  static get styles(): CSSResult[] {
    return [css`${unsafeCSS(styles)}`, css`${unsafeCSS(typographyStyles)}`];
  }

  private selectItem() {
    this.sizeLayerState.update({selectedArticle: this.article});
    this.dispatchEvent(new CustomEvent<SizeSelectionContainer>('ents:size-layer-item:selected', {
      detail: {
        identifier: ProductIdentifier.create(this.syan, this.colorId).id,
        colorId: this.colorId,
        articleId: this.article.id,
        salesInformationId: this.article.salesInformationId,
        triggerCta: this.article.isOutOfStock ? true : this.addToCartAfterSizeSelection,
      },
      bubbles: true,
      composed: true,
    }));
    trackSizeSelected(this.syan, this.article, this.colorId);
  }

  private renderSizeInformation() {
    return html`
      <div class="ents-size-and-stock-information">
        <div class="ents-size-information">
          <div data-test-ents-size-layer-item-text>${this.article.sizeOrQuantity ?? 'one size'}</div>
          <brtn-size-layer-inline-recommendation
            size=${this.article.sizeOrQuantity}
            article-id=${this.article.id}
            in-stock=${!this.article.isOutOfStock}
          ></brtn-size-layer-inline-recommendation>
        </div>
        <div>
          ${this.article.lowStockMessage ? html`
              <div class="ents-copy-extra-small ents-size-layer__item--message-low">${this.article.lowStockMessage}</div>`
            : nothing}
          ${this.article.isOutOfStock ? html`
              <div class="ents-copy-extra-small ents-size-layer__item--message-sold-out">
                ${hideIconFromAssistiveTechnology(bellIcon())}${this.article.requestSizeMessage}
              </div>`
            : nothing}

        </div>
      </div>`;
  }

  private renderBlackOrRedPrice() {
    if (this.article.price.isReduced && this.article.isOutOfStock) {
      return html`
        <p class="ents-copy-small ents-size-layer__item--black-price" data-test-ents-out-of-stock-red-price>
          ${this.article.price.currentPriceMessage}
        </p>`;
    } else if (this.article.price.isReduced) {
      return html`
        <p class="ents-copy-small ents-size-layer__item--red-price" data-test-ents-red-price>
          ${this.article.price.currentPriceMessage}
        </p>`;
    } else {
      return html`
        <p class="ents-copy-small ents-size-layer__item--black-price" data-test-ents-black-price>
          ${this.article.price.currentPriceMessage}
        </p>`;
    }
  }

  private renderPrice() {
    if (!this.showPrices) {
      return nothing;
    }
    return html`
      <div class="ents-size-layer__item--price">
        ${this.renderBlackOrRedPrice()}
        ${this.article.price.unitPriceMessage ? html`
          <p
            class="
                ents-copy-extra-small ents-size-layer__item--unit-price
                ${!this.article.isOutOfStock && 'ents-size-layer__item--unit-price-color'}
              "
            aria-label="${this.messages['ents.wishlist.product.price.unitPrice.label']} ${this.article.price.unitPriceMessage}"
            data-test-ents-unit-price
          >
              (${this.article.price.unitPriceMessage})
          </p>
        ` : nothing}
      </div>`;
  }

  protected render() {
    return html`
      <label class="ents-copy-small-bold ents-size-layer__item ${classMap({
        'ents-size-layer__item--disabled': this.article.isOutOfStock,
        'ents-size-layer__item--active': this.active,
      })}">
        <input type="checkbox" name="ents-wishlist-size-selector" ?checked="${this.active}"
               @change="${this.selectItem}"
               value="${this.article.sizeOrQuantity}"
               data-test-ents-size-layer-item-checkbox="${this.article.sizeOrQuantity}">
        ${this.renderSizeInformation()}
        ${this.renderPrice()}
      </label>
    `;
  }
}
